import { Button, Divider } from 'antd'
import { TimeObject } from 'components/Input/variations/Calendar/types'
import { I18nContext } from 'contexts/I18n/I18nProvider'
import React, { useContext } from 'react'
import './index.scss'

type TimeInputProps = {
  startTime: TimeObject
  setStartTime: React.Dispatch<React.SetStateAction<TimeObject>>
  endTime: TimeObject
  setEndTime: React.Dispatch<React.SetStateAction<TimeObject>>
  handleOnOk: () => void
  handleOncancel: () => void
}

type FieldType = 'minutes' | 'hours'

const fieldThreshold = {
  hours: 23,
  minutes: 59,
}

function handleOnFocus(e: React.FocusEvent<HTMLInputElement, Element>) {
  e.target.select()
}

function handleOnChange(e: React.ChangeEvent<HTMLInputElement>, fieldType: FieldType, setValue: React.Dispatch<React.SetStateAction<TimeObject>>) {
  let inputValue = Number(e.target.value)

  if (Number(inputValue) > fieldThreshold[fieldType]) {
    inputValue = fieldThreshold[fieldType]
  }

  setValue(prevValue => {
    return {
      hours: fieldType === 'hours' ? inputValue : prevValue.hours,
      minutes: fieldType === 'minutes' ? inputValue : prevValue.minutes,
    }
  })

  if (e.target.value.length === 2) {
    const nextSibling = document.getElementById(`${Number(e.target.id) + 1}`)

    if (nextSibling) {
      return nextSibling.focus()
    }

    e.target.blur()
  }
}

function handleOnAdd(fieldType: FieldType, setValue: React.Dispatch<React.SetStateAction<TimeObject>>) {
  setValue(prevValue => {
    const sum = prevValue[fieldType] + 1
    const newValue = sum > fieldThreshold[fieldType] ? 0 : sum

    return {
      hours: fieldType === 'hours' ? newValue : prevValue.hours,
      minutes: fieldType === 'minutes' ? newValue : prevValue.minutes,
    }
  })
}

function handleOnSubtract(fieldType: FieldType, setValue: React.Dispatch<React.SetStateAction<TimeObject>>) {
  setValue(prevValue => {
    const sub = prevValue[fieldType] - 1
    const newValue = sub < 0 ? fieldThreshold[fieldType] : sub

    return {
      hours: fieldType === 'hours' ? newValue : prevValue.hours,
      minutes: fieldType === 'minutes' ? newValue : prevValue.minutes,
    }
  })
}

export default function TimeInput({ startTime, setStartTime, endTime, setEndTime, handleOnOk, handleOncancel }: TimeInputProps) {
  const t = useContext(I18nContext)

  return (
    <div>
      <div className="time-input-container">
        <div className="time-input">
          <div className="hours-input">
            <div className="plus-sign" onClick={() => handleOnAdd('hours', setStartTime)}>+</div>
            <div className="number-input">
              <input
                id="1"
                type="number"
                name="startDateHours"
                value={startTime.hours}
                onChange={(e) => handleOnChange(e, 'hours', setStartTime)}
                onFocus={handleOnFocus}
                maxLength={2}
              />
            </div>
            <div className="minus-sign" onClick={() => handleOnSubtract('hours', setStartTime)}>-</div>
          </div>
          <div className="input-divider">:</div>
          <div className="minutes-input">
            <div className="plus-sign" onClick={() => handleOnAdd('minutes', setStartTime)}>+</div>
            <div className="number-input">
              <input
                id="2"
                type="number"
                name="startDateMinutes"
                value={startTime.minutes}
                onChange={(e) => handleOnChange(e, 'minutes', setStartTime)}
                onFocus={handleOnFocus}
                maxLength={2}
              />
            </div>
            <div className="minus-sign" onClick={() => handleOnSubtract('minutes', setStartTime)}>-</div>
          </div>
        </div>
        <Divider type="vertical" style={{ margin: 'auto 0', border: '1px solid rgba(0, 0, 0, 0.25)', height: '1.5rem' }} />
        <div className="time-input">
          <div className="hours-input">
            <div className="plus-sign" onClick={() => handleOnAdd('hours', setEndTime)}>+</div>
            <div className="number-input">
              <input
                id="3"
                type="number"
                name="startDateHours"
                value={endTime.hours}
                onChange={(e) => handleOnChange(e, 'hours', setEndTime)}
                onFocus={handleOnFocus}
                maxLength={2}
              />
            </div>
            <div className="minus-sign" onClick={() => handleOnSubtract('hours', setEndTime)}>-</div>
          </div>
          <div className="input-divider">:</div>
          <div className="minutes-input">
            <div className="plus-sign" onClick={() => handleOnAdd('minutes', setEndTime)}>+</div>
            <div className="number-input">
              <input
                id="4"
                type="number"
                name="startDateMinutes"
                value={endTime.minutes}
                onChange={(e) => handleOnChange(e, 'minutes', setEndTime)}
                onFocus={handleOnFocus}
                maxLength={2}
              />
            </div>
            <div className="minus-sign" onClick={() => handleOnSubtract('minutes', setEndTime)}>-</div>
          </div>
        </div>
      </div>
      <div className="modal-buttons-container">
        <Button onClick={handleOnOk}>{t('general.select_value')}</Button>
        <Button onClick={handleOncancel}>{t('general.cancel')}</Button>
      </div>
    </div>
  )
}
